import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import {
  formatDate,
  formatMoney,
  PROPERTY_SYSTEM_TYPE_LABELS,
} from "@mavatech/shared";
import { PageHeader } from "@/components/layout/Layout";
import {
  Badge,
  Button,
  Input,
  Select,
  SortTh,
  useSort,
  LoadError,
} from "@/components/ui";
import { trpc } from "@/lib/trpc";

const TONE: Record<
  string,
  "neutral" | "accent" | "success" | "warning" | "danger"
> = {
  draft: "neutral",
  sent: "accent",
  accepted: "success",
  rejected: "danger",
  expired: "warning",
};
const LABEL: Record<string, string> = {
  draft: "Entwurf",
  sent: "Gesendet",
  accepted: "Angenommen",
  rejected: "Abgelehnt",
  expired: "Abgelaufen",
};

export function Quotes() {
  const navigate = useNavigate();
  const list = trpc.quote.list.useQuery();

  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [systemType, setSystemType] = useState("");

  const properties = useMemo(() => {
    const map = new Map<string, { id: string; label: string }>();
    for (const q of list.data ?? []) {
      if (q.property)
        map.set(q.property.id, { id: q.property.id, label: q.property.label });
    }
    return [...map.values()];
  }, [list.data]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (list.data ?? []).filter((q) => {
      if (s) {
        const hay = [
          q.number,
          q.title,
          q.customer?.name ?? q.recipientName,
          q.property?.addressStreet,
          q.property?.addressCity,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(s)) return false;
      }
      if (from && q.issueDate < new Date(from)) return false;
      if (to && q.issueDate > new Date(`${to}T23:59:59`)) return false;
      if (propertyId && q.property?.id !== propertyId) return false;
      if (systemType && q.property?.systemType !== systemType) return false;
      return true;
    });
  }, [list.data, search, from, to, propertyId, systemType]);

  const { sorted, sort, toggle } = useSort(filtered, {
    customer: (q) => q.customer?.name ?? q.recipientName,
    number: (q) => q.number,
    property: (q) => q.property?.label,
    date: (q) => q.issueDate,
    status: (q) => LABEL[q.status] ?? q.status,
    amount: (q) => q.grossCents,
  });

  const hasFilters = search || from || to || propertyId || systemType;

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Angebote"
        actions={
          <Button variant="primary" onClick={() => navigate("/angebote/neu")}>
            <Plus className="w-4 h-4" /> Neues Angebot
          </Button>
        }
      />
      <div className="px-6 py-3 border-b border-border-subtle flex items-center gap-3 flex-wrap">
        <div className="relative max-w-xs flex-1">
          <Search className="w-4 h-4 text-text-tertiary absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            className="pl-8"
            placeholder="Kunde, Betreff, Nummer, Adresse …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Input
          fullWidth={false}
          className="w-40"
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          title="Datum von"
        />
        <Input
          fullWidth={false}
          className="w-40"
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          title="Datum bis"
        />
        <Select
          fullWidth={false}
          className="w-44"
          value={propertyId}
          onChange={(e) => setPropertyId(e.target.value)}
          options={[
            { value: "", label: "Alle Objekte" },
            ...properties.map((p) => ({ value: p.id, label: p.label })),
          ]}
        />
        <Select
          fullWidth={false}
          className="w-44"
          value={systemType}
          onChange={(e) => setSystemType(e.target.value)}
          options={[
            { value: "", label: "Alle Anlagentypen" },
            ...Object.entries(PROPERTY_SYSTEM_TYPE_LABELS).map(
              ([value, label]) => ({ value, label }),
            ),
          ]}
        />
        {hasFilters && (
          <button
            className="text-xs text-text-tertiary hover:text-text-primary"
            onClick={() => {
              setSearch("");
              setFrom("");
              setTo("");
              setPropertyId("");
              setSystemType("");
            }}
          >
            Filter zurücksetzen
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto overflow-x-auto">
        {list.isLoading ? (
          <p className="p-6 text-sm text-text-tertiary">Lädt …</p>
        ) : list.isError ? (
          <LoadError onRetry={() => list.refetch()} />
        ) : !filtered.length ? (
          <div className="p-10 text-center text-sm text-text-tertiary">
            {hasFilters ? "Keine Treffer." : "Noch keine Angebote."}
          </div>
        ) : (
          <>
            {/* Mobil: Karten statt Tabelle (AUDIT-2026-10-01-UX.md, Punkt 3.3). */}
            <div className="md:hidden divide-y divide-border-subtle">
              {sorted.map((q) => (
                <button
                  key={q.id}
                  onClick={() => navigate(`/angebote/${q.id}`)}
                  className="w-full text-left px-4 py-3 flex flex-col gap-1"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs text-text-tertiary">
                      {q.number ?? "—"}
                    </span>
                    <Badge tone={TONE[q.status] ?? "neutral"}>
                      {LABEL[q.status] ?? q.status}
                    </Badge>
                  </div>
                  <div className="font-medium text-text-primary">{q.title}</div>
                  <div className="text-text-tertiary text-xs">
                    {q.customer?.name ?? q.recipientName ?? "— ohne Kunde —"}
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1 text-sm">
                    <span className="text-text-tertiary text-xs">
                      {formatDate(q.issueDate as unknown as string)}
                    </span>
                    <span className="text-money text-text-primary">
                      {formatMoney(q.grossCents)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
            <table className="data-table hidden md:table">
              <thead>
                <tr>
                  <SortTh
                    label="Kunde / Betreff"
                    sortKey="customer"
                    sort={sort}
                    onSort={toggle}
                  />
                  <SortTh
                    label="Nummer"
                    sortKey="number"
                    sort={sort}
                    onSort={toggle}
                  />
                  <SortTh
                    label="Objekt"
                    sortKey="property"
                    sort={sort}
                    onSort={toggle}
                  />
                  <SortTh
                    label="Datum"
                    sortKey="date"
                    sort={sort}
                    onSort={toggle}
                  />
                  <SortTh
                    label="Status"
                    sortKey="status"
                    sort={sort}
                    onSort={toggle}
                  />
                  <SortTh
                    label="Betrag"
                    sortKey="amount"
                    sort={sort}
                    onSort={toggle}
                    className="text-right"
                  />
                </tr>
              </thead>
              <tbody>
                {sorted.map((q) => (
                  <tr
                    key={q.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/angebote/${q.id}`)}
                  >
                    <td className="text-text-primary">
                      <div className="font-medium">
                        {q.customer?.name ?? q.recipientName ?? (
                          <span className="text-text-tertiary font-normal">
                            — ohne Kunde —
                          </span>
                        )}
                      </div>
                      <div className="text-text-tertiary text-xs">
                        {q.title}
                      </div>
                    </td>
                    <td className="font-mono text-text-secondary">
                      {q.number ?? "—"}
                    </td>
                    <td className="text-text-tertiary text-xs">
                      {q.property?.label ?? "—"}
                    </td>
                    <td className="text-text-secondary">
                      {formatDate(q.issueDate as unknown as string)}
                    </td>
                    <td>
                      <Badge tone={TONE[q.status] ?? "neutral"}>
                        {LABEL[q.status] ?? q.status}
                      </Badge>
                    </td>
                    <td className="text-right text-money text-text-primary">
                      {formatMoney(q.grossCents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
